import { Link } from 'react-router-dom';
import type { Article } from '@/types';
import { formatDate, formatViews } from '@/utils/data';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'plain';
  showSummary?: boolean;
}

function CategoryTag({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
      {name}
    </span>
  );
}

export default function ArticleCard({ article, variant = 'default', showSummary = true }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-3 sm:gap-4 rounded-lg bg-background-100 p-3 cursor-pointer transition-colors hover:bg-primary-50"
      >
        <div className="w-28 h-20 sm:w-40 sm:h-28 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 色哟哟娱乐资讯`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            {showSummary && (
              <p className="mt-1 hidden sm:block text-xs text-foreground-600 line-clamp-2">{article.summary}</p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <CategoryTag name={article.categoryName} />
            <span>{formatDate(article.date)}</span>
            <span className="hidden sm:inline-flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'plain') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group block cursor-pointer border-b border-background-200 py-3 last:border-b-0"
      >
        <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
          <span className="text-primary-600">{article.categoryName}</span>
          <span>·</span>
          <span>{formatDate(article.date)}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg bg-background-100 cursor-pointer transition-colors hover:bg-background-200/70"
    >
      <div className="w-full h-44 sm:h-48 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 色哟哟娱乐资讯`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2">
          <CategoryTag name={article.categoryName} />
          {article.hot && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
              <i className="ri-fire-fill"></i>
              热
            </span>
          )}
        </div>
        <h3 className="mt-2.5 text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        {showSummary && (
          <p className="mt-2 flex-1 text-sm text-foreground-600 line-clamp-2">{article.summary}</p>
        )}
        <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
          <span>{formatDate(article.date)}</span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}